import { Search as SearchIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRepo, type Article } from '../lib/repo';
import { useDebounced } from '../lib/hooks';
import { Dialog, Input, Spinner } from './ui';
import { relativeTime } from '../lib/format';

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 220);
  const [results, setResults] = useState<Article[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setQ(''); setResults(null); return; }
  }, [open]);

  useEffect(() => {
    if (!debounced.trim()) { setResults(null); return; }
    let live = true;
    setBusy(true);
    getRepo()
      .listPublicArticles({ query: debounced.trim(), limit: 8 })
      .then((r) => { if (live) setResults(r); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [debounced]);

  return (
    <Dialog open={open} onClose={onClose} title="Search The Story Line">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles, authors, topics…" className="pl-9" aria-label="Search articles" />
      </div>
      <div className="mt-4 min-h-24">
        {busy ? <Spinner label="Searching…" /> : null}
        {!busy && results && results.length === 0 ? <p className="py-6 text-center text-sm text-muted">No results for “{debounced}”.</p> : null}
        {!busy && !results ? <p className="py-6 text-center text-sm text-muted">Start typing to search the archive.</p> : null}
        <ul className="divide-y divide-border">
          {(results ?? []).map((a) => (
            <li key={a.id}>
              <Link to={`/article/${a.slug}`} onClick={onClose} className="flex items-center gap-3 py-3 hover:bg-background">
                {a.coverImageUrl ? <img src={a.coverImageUrl} alt="" className="h-14 w-20 shrink-0 rounded object-cover" loading="lazy" /> : null}
                <span className="min-w-0">
                  <span className="block truncate font-medium">{a.title}</span>
                  <span className="block truncate text-xs text-muted">{a.category?.name} · {relativeTime(a.publishedAt)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
